<template>
  <div
    class="constructor-work-item"
    :class="{ 'constructor-work-item--active': active }"
    @click="$emit('activate')"
  >
    <!-- Header -->
    <div class="constructor-work-item-header">
      <span
        class="constructor-work-item-toggle"
        title="Свернуть / Развернуть"
        @click.stop="$emit('toggle-collapse')"
      >
        <feather-icon
          :icon="item.collapsed ? 'ChevronRightIcon' : 'ChevronDownIcon'"
          size="14"
        />
      </span>
      <feather-icon
        icon="GitMergeIcon"
        size="14"
        class="constructor-work-item-join-icon"
      />
      <span class="constructor-work-item-cmd">{{ item.joinName || item.command }}</span>
      <span
        class="constructor-work-item-close"
        title="Убрать"
        @click.stop="$emit('remove')"
      >
        <feather-icon
          icon="XIcon"
          size="14"
        />
      </span>
    </div>

    <template v-if="!item.collapsed">
      <div class="constructor-work-item-body">
        <div class="constructor-join-fields">

          <!-- id (readonly) -->
          <cbs-inline-attribute
            label="id:"
            :value="item.joinId"
            :editable="false"
            :focused="focusedRowIndex === 0"
            @click="focusRow(0)"
          />

          <!-- sid -->
          <cbs-inline-attribute
            ref="sidAttr"
            label="sid:"
            :value="item.joinSid"
            :focused="focusedRowIndex === 1"
            @click="focusRow(1)"
            @save="val => saveField('sid', 'joinSid', val)"
          />

          <!-- name -->
          <cbs-inline-attribute
            ref="nameAttr"
            label="name:"
            :value="item.joinName"
            :focused="focusedRowIndex === 2"
            @click="focusRow(2)"
            @save="val => saveField('name', 'joinName', val)"
          />

          <!-- descr -->
          <cbs-inline-attribute
            ref="descrAttr"
            label="descr:"
            :value="item.joinDescr"
            :focused="focusedRowIndex === 3"
            @click="focusRow(3)"
            @save="val => saveField('descr', 'joinDescr', val)"
          />

          <!-- sourceobject -->
          <cbs-inline-attribute
            ref="objAttr"
            label="объект:"
            type="ref"
            :value="sourceObjectId"
            :display-val="item.sourceobject && typeof item.sourceobject === 'object' ? item.sourceobject.title : (item.sourceobject || '')"
            :options="objectOptions"
            label-key="title"
            placeholder="Поиск объекта..."
            :focused="focusedRowIndex === 4"
            @click="focusRow(4)"
            @select="selectObject"
          >
            <template #actions>
              <span
                v-if="sourceObjectId"
                title="Открыть объект"
                class="constructor-step-open-link"
                @click.stop="openObjectTab(sourceObjectId)"
              >
                <feather-icon
                  icon="ExternalLinkIcon"
                  size="12"
                />
              </span>
              <span
                v-if="sourceObjectId"
                title="Добавить в рабочую область"
                class="constructor-step-add-work"
                @click.stop="$emit('add-work-object', sourceObjectId)"
              >
                <feather-icon
                  icon="PlusCircleIcon"
                  size="12"
                />
              </span>
            </template>
          </cbs-inline-attribute>

          <!-- jsonfilter -->
          <cbs-inline-attribute
            ref="filterAttr"
            label="фильтр:"
            type="filter"
            :filter-obj="filterObj"
            :fields="sourceFields"
            :focused="focusedRowIndex === 5"
            @click="focusRow(5)"
            @save="saveFilter"
          />

        </div>
      </div>
    </template>
  </div>
</template>

<script>
import CbsInlineAttribute from '@/cubus/components/inline-attribute/CbsInlineAttribute.vue'

function parseFilter(jsonfilter) {
  let obj = null
  if (jsonfilter) {
    try {
      obj = typeof jsonfilter === 'string' ? JSON.parse(jsonfilter) : jsonfilter
    } catch (e) {
      obj = null
    }
  }
  if (!obj || !obj.node) {
    obj = {
      node: {
        isactive: false,
        isexpanded: false,
        oper: { id: 1, sid: 'and', name: 'AND' },
        nodes: [],
        conds: [],
      },
    }
  }
  return obj
}

export default {
  name: 'CbsConstructorJoinCard',
  components: {
    CbsInlineAttribute,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    allObjects: {
      type: Array,
      default: () => [],
    },
    objectFieldsCache: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      filterObj: parseFilter(this.item.jsonfilter),
      focusedRowIndex: -1,
    }
  },
  computed: {
    objectOptions() {
      return this.allObjects.map(o => ({ value: o.id, title: o.name }))
    },
    sourceObjectId() {
      const src = this.item.sourceobject
      return (src && typeof src === 'object' ? src.value : null) || this.item.sourceobjectId || null
    },
    sourceFields() {
      const cached = this.objectFieldsCache[this.sourceObjectId]
      return (cached && cached.fields) || []
    },
  },
  watch: {
    sourceObjectId: {
      immediate: true,
      handler(objectId) {
        if (objectId && !this.objectFieldsCache[objectId]) {
          this.$emit('load-object-fields', objectId)
        }
      },
    },
    active(val) {
      if (!val) this.focusedRowIndex = -1
    },
  },
  mounted() {
    this.cbs_keyHandler = e => this.onKeydown(e)
    document.addEventListener('keydown', this.cbs_keyHandler)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.cbs_keyHandler)
  },
  methods: {
    focusRow(index) {
      this.focusedRowIndex = index
    },
    scrollToFocused() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.cbs-inline-attr--focused')
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    },
    onKeydown(e) {
      if (!this.active || this.focusedRowIndex < 0) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      const { key } = e
      const ROW_COUNT = 6

      if (key === 'ArrowDown') {
        e.preventDefault()
        if (this.focusedRowIndex < ROW_COUNT - 1) {
          this.focusedRowIndex += 1
          this.scrollToFocused()
        }
      } else if (key === 'ArrowUp') {
        e.preventDefault()
        if (this.focusedRowIndex > 0) {
          this.focusedRowIndex -= 1
          this.scrollToFocused()
        }
      } else if (key === 'ArrowRight') {
        e.preventDefault()
        if (this.focusedRowIndex === 1) { if (this.$refs.sidAttr) this.$refs.sidAttr.startEdit() } else if (this.focusedRowIndex === 2) { if (this.$refs.nameAttr) this.$refs.nameAttr.startEdit() } else if (this.focusedRowIndex === 3) { if (this.$refs.descrAttr) this.$refs.descrAttr.startEdit() } else if (this.focusedRowIndex === 4) { if (this.$refs.objAttr) this.$refs.objAttr.toggle() } else if (this.focusedRowIndex === 5) { if (this.$refs.filterAttr) this.$refs.filterAttr.toggleFilter() }
      } else if (key === 'ArrowLeft') {
        e.preventDefault()
        if (this.focusedRowIndex === 4) { if (this.$refs.objAttr) this.$refs.objAttr.close() } else if (this.focusedRowIndex === 5) { if (this.$refs.filterAttr) this.$refs.filterAttr.toggleFilter() }
      } else if (key === 'Enter') {
        e.preventDefault()
        if (this.focusedRowIndex === 1) { if (this.$refs.sidAttr) this.$refs.sidAttr.startEdit() } else if (this.focusedRowIndex === 2) { if (this.$refs.nameAttr) this.$refs.nameAttr.startEdit() } else if (this.focusedRowIndex === 3) { if (this.$refs.descrAttr) this.$refs.descrAttr.startEdit() } else if (this.focusedRowIndex === 4) { if (this.$refs.objAttr) this.$refs.objAttr.toggle() } else if (this.focusedRowIndex === 5) { if (this.$refs.filterAttr) this.$refs.filterAttr.toggleFilter() }
      } else if (key === 'Escape') {
        if (this.$refs.objAttr) this.$refs.objAttr.close()
        if (this.$refs.filterAttr) this.$refs.filterAttr.filterExpanded = false
      }
    },
    saveField(apiField, itemField, val) {
      const current = this.item[itemField] || ''
      if (val !== current) {
        this.$emit('update-field', { field: apiField, value: val, itemField })
      }
    },
    selectObject(obj) {
      this.$emit('update-field', {
        // field: 'sourceobjectid', value: obj.value, title: obj.title, itemField: 'sourceobjectId', titleField: 'sourceobject',
        field: 'sourceobject', value: obj.value, title: obj.title, itemField: 'sourceobject', titleField: 'sourceobject',
      })
    },
    openObjectTab(objectId) {
      const route = this.$router.resolve({ name: 'cubus-object', params: { objectid: objectId } })
      window.open(route.href, '_blank')
    },
    saveFilter() {
      this.$emit('update-field', { field: 'jsonfilter', value: this.filterObj, itemField: 'jsonfilter' })
    },
  },
}
</script>

<style lang="scss" scoped>
.constructor-work-item {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  overflow: visible;
}

.constructor-work-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8f8f8;
  font-size: 13px;
  font-family: monospace;
  color: #6e6b7b;
  border-bottom: 1px solid #ebe9f1;
}

.constructor-work-item-toggle {
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  color: #b9b9c3;
  &:hover { color: #5e5873; }
}

.constructor-work-item-join-icon {
  color: #7367f0;
  flex-shrink: 0;
}

.constructor-work-item-cmd {
  font-weight: 600;
  color: #7367f0;
  flex: 1;
}

.constructor-work-item-close {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  &:hover { color: #ea5455; background: rgba(234, 84, 85, 0.1); }
}

.constructor-work-item--active > .constructor-work-item-header {
  background: #7367f0;
  color: #fff;
  border-color: #7367f0;

  .constructor-work-item-toggle { color: rgba(255,255,255,0.7); &:hover { color: #fff; } }
  .constructor-work-item-cmd { color: #fff; }
  .constructor-work-item-join-icon { color: rgba(255,255,255,0.9); }
  .constructor-work-item-close { color: rgba(255,255,255,0.7); &:hover { color: #fff; background: rgba(255,255,255,0.15); } }
}

.constructor-work-item-body {
  padding: 12px;
  font-size: 14px;
}

.constructor-join-fields {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.constructor-step-open-link {
  color: #b9b9c3;
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;
  &:hover { color: #7367f0; background: rgba(115, 103, 240, 0.1); }
}

.constructor-step-add-work {
  color: #b9b9c3;
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;
  &:hover { color: #28c76f; background: rgba(40, 199, 111, 0.1); }
}

// Dark mode
.dark-layout {
  .constructor-work-item { border-color: #3b4253; }
  .constructor-work-item-header { background: #242b3d; border-color: #3b4253; color: #b4b7bd; }
  .constructor-work-item--active > .constructor-work-item-header { background: #7367f0; border-color: #7367f0; }
}
</style>
